//src/admin/pages/AdminCreateDiscount.js
import React, { useState, useContext, useEffect, useMemo } from "react";
import { useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { FaBox, FaCalendarAlt, FaClock, FaSearch } from "react-icons/fa";
import { FiChevronRight, FiAlertCircle } from "react-icons/fi";
import {
  getAdminProducts,
  getAdminCategories,
  createAdminDiscount,
} from "../../services/adminProducts.api";
import { removeProductsFromSale } from "../../services/adminProducts.api";
import { getAdminCustomers } from "../../services/adminCustomers.api";
import { updateAdminDiscount } from "../../services/adminDiscounts.api";
import { getAdminDiscountById } from "../../services/adminDiscounts.api";

function AdminCreateDiscount() {
  const navigate = useNavigate();
const { id } = useParams();

const isEdit = !!id;

  const customerPickerRef = useRef(null);
  const { lang } = useContext(AdminLanguageContext);
  const [isOpen, setIsOpen] = useState(false);

  // ================= STATE =================
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");

  const [saving, setSaving] = useState(false);

  const [appliesToType, setAppliesToType] = useState("products");

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectionModalOpen, setSelectionModalOpen] = useState(false);

  const [isActive, setIsActive] = useState(1); // 1 = active, 0 = inactive

  // ================= CUSTOMERS =================
const [customers, setCustomers] = useState([]);
const [customerSearch, setCustomerSearch] = useState("");

const [selectedCustomers, setSelectedCustomers] = useState([]);

const [eligibility, setEligibility] = useState("all"); // "all" | "specific"

const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const [minRequirement, setMinRequirement] = useState("none");
  const [minAmount, setMinAmount] = useState("");
  const [minQty, setMinQty] = useState("");

  const [productSearch, setProductSearch] = useState("");

  const [usageLimitType, setUsageLimitType] = useState("none");
  const [usageLimit, setUsageLimit] = useState("");

  const [confirmRemoveSaleOpen, setConfirmRemoveSaleOpen] = useState(false);
  const [removeSaleProduct, setRemoveSaleProduct] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [hasEnd, setHasEnd] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  const [loadingDiscount, setLoadingDiscount] = useState(isEdit);

  const [categorySearch, setCategorySearch] = useState("");

  // DATE MODAL
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateTarget, setDateTarget] = useState(null); // "start" | "end"

  // TIME MODAL
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [timeTarget, setTimeTarget] = useState(null); // "start" | "end"

  // calendar temp
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  const [tempTime, setTempTime] = useState({
  hour: 12,
  minute: "00",
  period: "AM",
});

  // ================= UI =================
  const card = "bg-white border border-[#e1e3e5] rounded-none sm:rounded-[18px] shadow-sm p-4 space-y-4";

  const subtitle = "text-[13px] text-[#6d7175]";

  const input =
  "w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm outline-none bg-white text-[#202223] focus:border-[#008060] focus:ring-1 focus:ring-[#008060] transition";

const select =
  "appearance-none w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm outline-none bg-white text-[#202223] focus:border-[#008060] transition";

const smallInput =
  "h-[36px] rounded-[10px] border border-[#c9cccf] px-3 text-sm outline-none bg-white text-[#202223] focus:border-[#008060]";

  const formatDisplayDate = (date) => {
  if (!date) return new Date().toLocaleDateString();
  return new Date(date).toLocaleDateString();
};

const formatDisplayTime = (time) => {
  if (!time) {
    return new Date().toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const [h, m] = time.split(":");
  const date = new Date();
  date.setHours(h, m);

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const filteredProducts = useMemo(() => {
  const q = productSearch.trim().toLowerCase();

  if (!q) return products;

  return products.filter((p) => {
    const id = (p.public_id || "").toLowerCase();
    const nameEn = (p.name_en || "").toLowerCase();
    const nameAr = (p.name_ar || "").toLowerCase();

    // 🔥 always search EVERYTHING (no dropdown needed)
    return (
      id.includes(q) ||
      nameEn.includes(q) ||
      nameAr.includes(q)
    );
  });
}, [products, productSearch]);

const filteredCategories = useMemo(() => {
  const q = categorySearch.trim().toLowerCase();

  if (!q) return categories;

  return categories.filter((c) => {
    const nameEn = (c.name_en || "").toLowerCase();
    const nameAr = (c.name_ar || "").toLowerCase();

    return nameEn.includes(q) || nameAr.includes(q);
  });
}, [categories, categorySearch]);

const CalendarRangePicker = ({ rangeStart, rangeEnd, setRangeStart, setRangeEnd }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

  const startDay = startOfMonth.getDay();

  const days = [];

  for (let i = 0; i < startDay; i++) days.push(null);

  for (let d = 1; d <= endOfMonth.getDate(); d++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
  }

  const isSameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const handleClick = (day) => {
    setRangeStart(day);
    setRangeEnd(day);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3 px-2">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>‹</button>

        <div className="font-semibold text-sm">
          {currentMonth.toLocaleString(lang === "AR" ? "ar" : "en", {
            month: "long",
            year: "numeric",
          })}
        </div>

        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>›</button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          if (!day) return <div key={i} />;

          const selected = isSameDay(day, rangeStart);

          return (
            <button
              key={i}
              onClick={() => handleClick(day)}
              className={`h-8 rounded-md text-sm ${
                selected ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TimePicker = ({ tempTime, setTempTime }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = ["00", "15", "30", "45"];
  const periods = ["AM", "PM"];

  return (
    <div className="flex gap-3">

      {/* HOURS */}
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-2">Hour</div>
        <div className="grid grid-cols-3 gap-2">
          {hours.map(h => (
            <button
              key={h}
              onClick={() => setTempTime(prev => ({ ...prev, hour: h }))}
              className={`
                h-9 rounded-lg text-sm
                ${tempTime.hour === h ? "bg-black text-white" : "hover:bg-gray-200"}
              `}
            >
              {h}
            </button>
          ))}
        </div>
      </div>

      {/* MINUTES */}
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-2">Min</div>
        <div className="flex flex-col gap-2">
          {minutes.map(m => (
            <button
              key={m}
              onClick={() => setTempTime(prev => ({ ...prev, minute: m }))}
              className={`
                h-9 rounded-lg text-sm
                ${tempTime.minute === m ? "bg-black text-white" : "hover:bg-gray-200"}
              `}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* AM / PM */}
      <div className="flex flex-col gap-2">
        <div className="text-xs text-gray-500 mb-2"></div>
        {periods.map(p => (
          <button
            key={p}
            onClick={() => setTempTime(prev => ({ ...prev, period: p }))}
            className={`
              h-9 px-3 rounded-lg text-sm
              ${tempTime.period === p ? "bg-black text-white" : "hover:bg-gray-200"}
            `}
          >
            {p}
          </button>
        ))}
      </div>

    </div>
  );
};

const filteredCustomers = useMemo(() => {
  const q = customerSearch.trim().toLowerCase();

  if (!q) return customers.slice(0, 8);

  return customers.filter((c) =>
    (c.name || "").toLowerCase().includes(q) ||
    (c.email || "").toLowerCase().includes(q) ||
    (c.phone || "").toLowerCase().includes(q)
  );
}, [customers, customerSearch]);

const buildSummary = () => {
  if (!discountValue) return "Start configuring your discount...";

  // TYPE
  const typeText =
    discountType === "percentage"
      ? `${discountValue}% off`
      : `$${discountValue} off`;

  // APPLIES TO
  const appliesText =
      appliesToType === "products"
        ? selectedProducts.length || selectedCategories.length
          ? `${selectedProducts.length || selectedCategories.length} selected products`
          : "specific products"
        : selectedProducts.length || selectedCategories.length
        ? `${selectedProducts.length || selectedCategories.length} selected collections`
        : "specific collections";

  // ELIGIBILITY
  const eligibilityText =
    eligibility === "all"
      ? "all customers"
      : "specific customers";

  // MIN REQUIREMENT
  let minText = "";
  if (minRequirement === "amount" && minAmount) {
    minText = `with a minimum purchase of $${minAmount}`;
  }
  if (minRequirement === "qty" && minQty) {
    minText = `with a minimum of ${minQty} items`;
  }

  // USAGE
  let usageText = "";
  if (usageLimitType === "limit" && usageLimit) {
    usageText = `limited to ${usageLimit} uses`;
  }
  if (usageLimitType === "once") {
    usageText = `one use per customer`;
  }

  // DATES
  const start =
    startDate
      ? `${formatDisplayDate(startDate)} at ${formatDisplayTime(startTime)}`
      : "now";

  const end =
    hasEnd && endDate
      ? `${formatDisplayDate(endDate)} at ${formatDisplayTime(endTime)}`
      : null;

  const dateText = end
    ? `active from ${start} to ${end}`
    : `starting from ${start}`;

  // FINAL SENTENCE
  return `
    This discount gives ${typeText} on ${appliesText} for ${eligibilityText}
    ${minText ? ", " + minText : ""}
    ${usageText ? ", " + usageText : ""}
    , ${dateText}.
  `;
};

const isSaveDisabled =
  saving ||
  !discountValue ||
  Number(discountValue) <= 0 ||
  (
    appliesToType === "products"
      ? selectedProducts.length === 0
      : selectedCategories.length === 0
  );

const handleSave = async () => {
  if (isSaveDisabled) return;

  try {
    setSaving(true);

    const payload = {
  discountType: discountType,
  discountValue: Number(discountValue),
  appliesToType: appliesToType,

  // 🔥 KEEP THESE SAME (backend expects these names)
  selectedProducts,
  selectedCategories,
  eligibility: "all",

  minRequirement,
  minAmount,
  minQty,

  usageLimitType,
  usageLimit,

  startDate,
  startTime,
  hasEnd,
  endDate,
  endTime,
  is_active: isActive,
};

if (isEdit) {
  await updateAdminDiscount(id, payload);
} else {
  await createAdminDiscount(payload);
}

    setIsDirty(false);
    setShowConfirm(false);
    navigate("/admin/discounts");
  } catch (err) {
    console.error("Failed to create discount:", err);
    alert(
      lang === "AR"
        ? "فشل إنشاء الخصم"
        : "Failed to create discount"
    );
  } finally {
    setSaving(false);
  }
};

const handleNavigate = (path) => {
  if (!isDirty) {
    navigate(path);
  } else {
    setPendingNavigation(() => () => navigate(path));
    setShowLeaveConfirm(true);
  }
};

useEffect(() => {
  getAdminCustomers().then(setCustomers).catch(() => {});
}, []);

useEffect(() => {
  if (!isEdit) return;

  const loadDiscount = async () => {
    try {
      setLoadingDiscount(true);

      const d = await getAdminDiscountById(id);

      if (!d) {
        console.error("Discount not found");
        navigate("/admin/discounts");
        return;
      }

      setIsActive(d.is_active ?? 1);
      setDiscountType(d.discount_type || "percentage");
      setDiscountValue(String(d.discount_value || ""));
      setAppliesToType(d.applies_to || "products");

      setMinRequirement(d.min_requirement || "none");
      setMinAmount(d.min_amount != null ? String(d.min_amount) : "");
      setMinQty(d.min_qty != null ? String(d.min_qty) : "");

      if (d.once_per_customer) {
        setUsageLimitType("once");
        setUsageLimit("");
      } else if (d.usage_limit) {
        setUsageLimitType("limit");
        setUsageLimit(String(d.usage_limit));
      } else {
        setUsageLimitType("none");
        setUsageLimit("");
      }

      if (d.start_at) {
  const dt = new Date(d.start_at);

  const date = dt.toISOString().slice(0, 10); // YYYY-MM-DD
  const time = dt.toTimeString().slice(0, 5); // HH:MM

  setStartDate(date);
  setStartTime(time);
} else {
  setStartDate("");
  setStartTime("");
}

      if (d.end_at) {
  const dt = new Date(d.end_at);

  const date = dt.toISOString().slice(0, 10);
  const time = dt.toTimeString().slice(0, 5);

  setHasEnd(true);
  setEndDate(date);
  setEndTime(time);
} else {
  setHasEnd(false);
  setEndDate("");
  setEndTime("");
}

      setSelectedProducts(
        (d.products || []).map((p) => ({
          id: p.id,
          name: lang === "AR"
            ? (p.name_ar || p.name_en || "Product")
            : (p.name_en || p.name_ar || "Product"),
        }))
      );

      setSelectedCategories(
        (d.categories || []).map((c) => ({
          id: c.id,
          name: lang === "AR"
            ? (c.name_ar || c.name_en || "Collection")
            : (c.name_en || c.name_ar || "Collection"),
        }))
      );

      setIsDirty(false);
    } catch (err) {
      console.error("Failed to load discount:", err);
    } finally {
      setLoadingDiscount(false);
    }
  };

  loadDiscount();
}, [id, isEdit, lang, navigate]);

useEffect(() => {
  function handleClickOutside(e) {
    if (
      customerPickerRef.current &&
      !customerPickerRef.current.contains(e.target)
    ) {
      setCustomerPickerOpen(false);
    }
  }

  if (customerPickerOpen) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [customerPickerOpen]);

useEffect(() => {
  if (
    discountValue ||
    selectedProducts.length > 0 ||
    selectedCategories.length > 0 ||
    minAmount ||
    minQty ||
    usageLimit ||
    startDate ||
    endDate
  ) {
    setIsDirty(true);
  }
}, [
  discountValue,
  selectedProducts,
  selectedCategories,
  selectedCustomers,
  minAmount,
  minQty,
  usageLimit,
  startDate,
  endDate,
]);

useEffect(() => {
  getAdminProducts().then(setProducts).catch(() => {});
  getAdminCategories().then(setCategories).catch(() => {});
}, []);

useEffect(() => {
  getAdminProducts().then(setProducts).catch(() => {});
  getAdminCategories().then(setCategories).catch(() => {});
}, []);

if (loadingDiscount) {
  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "AR" ? "lg:mr-26" : "lg:ml-64"}`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6">
          <div className="max-w-[1040px] mx-auto">
            <div className="bg-white border border-[#e1e3e5] rounded-[18px] shadow-sm p-8 text-sm text-[#6d7175]">
              {lang === "AR" ? "جارٍ تحميل بيانات الخصم..." : "Loading discount data..."}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "AR" ? "lg:mr-26" : "lg:ml-64"}`}>
        <AdminHeader
          setIsOpen={setIsOpen}
          customCenter={
            <div
              className={`
                hidden sm:block
                w-full
                sm:max-w-[500px]
                max-w-full
                transition-all duration-300
                ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
              `}
            >
              <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 sm:py-[4px] space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between">

                {/* LEFT */}
                <div className="flex items-center gap-2 text-[13px] text-gray-200 justify-between sm:justify-start">
                  <FiAlertCircle className="text-white text-[16px]" />
                  <span>
                    {isEdit
          ? (lang === "AR" ? "تعديلات غير محفوظة" : "Unsaved changes")
          : (lang === "AR" ? "خصم غير محفوظ" : "Unsaved discount")}
                  </span>
                </div>

                {/* RIGHT */}
                <div className="flex gap-2 sm:gap-1.5 w-full sm:w-auto">

                  {/* DISCARD */}
                  <button
                    type="button"
                    onClick={() => handleNavigate("/admin/discounts")}
                    className="
                      px-2 py-[2px]
                      text-[12px]
                      rounded-md
                      border border-[#3a3a3a]
                      bg-[#1f1f1f]
                      hover:bg-[#363636]
                      text-gray-200
                      transition
                    "
                  >
                    {lang === "AR" ? "إلغاء" : "Discard"}
                  </button>

                  {/* SAVE */}
                  <button
                      type="button"
                      onClick={() => setShowConfirm(true)}
                      disabled={isSaveDisabled}
                      className={`
                        px-2 py-[2px]
                        text-[12px]
                        rounded-md
                        font-medium
                        transition
                        ${
                          isSaveDisabled
                            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                            : "bg-white text-black hover:bg-gray-200"
                        }
                      `}
                    >
                      {saving ? (lang === "AR" ? "جارٍ الحفظ..." : "Saving...") : (lang === "AR" ? "حفظ" : "Save")}
                    </button>

                </div>
              </div>
            </div>
          }
        />

        <main className="xl:p-3 sm:p-5">
         <div className="max-w-[1040px] mx-auto">

          {/* HEADER (same as product page) */}
          <div className="flex items-center gap-2 mb-5">
            <button
              onClick={() => handleNavigate("/admin/discounts")}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-200"
            >
              <FaBox />
            </button>

            <FiChevronRight />

            <span className="font-semibold text-[#202223]">
              {isEdit
                ? (lang === "AR" ? "تعديل خصم" : "Edit Discount")
                : (lang === "AR" ? "إنشاء خصم" : "Create Discount")}
            </span>
          </div>

<div className="sm:hidden mb-4 flex gap-2">
  <button
    type="button"
    onClick={() => handleNavigate("/admin/discounts")}
    className="flex-1 h-[42px] rounded-[12px] border border-[#c9cccf] bg-white text-[#202223] font-medium"
  >
    {lang === "AR" ? "إلغاء" : "Discard"}
  </button>

  <button
    type="button"
    onClick={() => setShowConfirm(true)}
    disabled={isSaveDisabled}
    className={`flex-1 h-[42px] rounded-[12px] font-medium transition ${
      isSaveDisabled
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-[#202223] text-white hover:bg-black"
    }`}
  >
    {saving ? (lang === "AR" ? "جارٍ الحفظ..." : "Saving...") : (lang === "AR" ? "حفظ" : "Save")}
  </button>
</div>

          {/* GRID */}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-3">

            {/* ================= LEFT ================= */}
            <div className="space-y-3">

              {/* SECTION 1 */}
              <div className={card}>
                <h3 className="text-[15px] font-semibold text-[#202223]">
                  {lang === "AR" ? "نوع الخصم" : "Discount type"}
                </h3>

                <p className={subtitle}>
                  {lang === "AR"
                    ? "اختر نوع الخصم (نسبة أو مبلغ ثابت) وحدد قيمة الخصم"
                    : "Select discount type (percentage or fixed) and set its value"}
                </p>

                <div className="flex gap-3 items-center">

                {/* BIG SELECT */}
                <select
                  value={discountType}
                  onChange={(e)=>setDiscountType(e.target.value)}
                  className="w-[200px] h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm focus:border-[#008060]"
                >
                  <option value="percentage">Percentage %</option>
                  <option value="fixed">Fixed amount</option>
                </select>

                {/* SMALL INPUT */}
                <div className="relative w-[140px]">
                  <input
                    type="number"
                    value={discountValue}
                    onChange={(e)=>setDiscountValue(e.target.value)}
                    placeholder="0"
                    className="w-full h-[42px] rounded-[12px] border border-[#c9cccf] pl-3 pr-10 text-sm focus:border-[#008060]"
                  />

                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6d7175] text-sm font-medium">
                    {discountType === "fixed" ? "$" : "%"}
                  </div>
                </div>

              </div>
              </div>

              {/* SECTION 2 */}
              <div className={card}>
                <h3 className="text-[15px] font-semibold text-[#202223]">
                  {lang === "AR" ? "ينطبق على" : "Applies to"}
                </h3>

                <p className={subtitle}>
                  {lang === "AR"
                    ? "حدد المنتجات أو المجموعات التي سيتم تطبيق الخصم عليها"
                    : "Choose which products or collections this discount applies to"}
                </p>

                <div className="relative">
                  <select
                    value={appliesToType}
                    onChange={(e)=>setAppliesToType(e.target.value)}
                    className="appearance-none w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white focus:border-[#008060]"
                  >
                    <option value="products">Specific products</option>
                    <option value="collections">Specific collection</option>
                  </select>

                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6d7175]">
                    ▾
                  </div>
                </div>
                {/* SEARCH + BROWSE */}
                <div className="flex gap-2 items-stretch">

                  <input
                      placeholder={lang === "AR" ? "ابحث..." : "Search..."}
                      value={appliesToType === "products" ? productSearch : categorySearch}
                      onChange={(e) => {
                        if (appliesToType === "products") {
                          setProductSearch(e.target.value);
                        } else {
                          setCategorySearch(e.target.value);
                        }
                        setSelectionModalOpen(true);
                      }}
                      onFocus={() => setSelectionModalOpen(true)}
                      className="flex-1 h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm"
                    />

                    <button
                      onClick={() => setSelectionModalOpen(true)}
                      className="w-[110px] h-[42px] rounded-[12px] border border-[#c9cccf] bg-white"
                    >
                      Browse
                    </button>

                </div>

                {/* SELECTED */}
                {selectedProducts.length === 0 && selectedCategories.length === 0 ? (
                  <div className="text-xs text-[#6d7175]">
                    {lang === "AR" ? "لم يتم اختيار عناصر" : "No items selected"}
                  </div>
                ) : (
                  <div className="space-y-3">

                    {/* ================= PRODUCTS ================= */}
                    {selectedProducts.length > 0 && (
                      <div className="bg-white border border-[#e1e3e5] rounded-[14px] overflow-hidden">

                        <div className="px-4 py-3 border-b bg-[#f6f6f7] text-xs font-medium text-[#6d7175]">
                          {lang === "AR" ? "المنتجات المختارة" : "Selected products"}
                        </div>

                        <div className="max-h-[200px] overflow-y-auto">
                          {selectedProducts.map((item, i) => {
                            const product = products.find(p => p.id === item.id);

                            return (
                              <div
                                key={i}
                                className="flex items-center justify-between px-4 py-3 border-b last:border-0"
                              >
                                <div className="flex items-center gap-3">

                                  <div className="w-10 h-10 rounded bg-[#f1f2f3] overflow-hidden">
                                    {product?.image && (
                                      <img src={product.image} className="w-full h-full object-cover" />
                                    )}
                                  </div>

                                  <div>
                                    <div className="text-sm font-medium">{item.name}</div>
                                    <div className="text-xs text-[#6d7175]">
                                      {product?.public_id}
                                    </div>
                                  </div>
                                </div>

                                <button
                                  onClick={() =>
                                    setSelectedProducts(prev =>
                                      prev.filter((_, index) => index !== i)
                                    )
                                  }
                                  className="text-xs text-red-500"
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ================= CATEGORIES ================= */}
                    {selectedCategories.length > 0 && (
                      <div className="bg-white border border-[#e1e3e5] rounded-[14px] overflow-hidden">

                        <div className="px-4 py-3 border-b bg-[#f6f6f7] text-xs font-medium text-[#6d7175]">
                          {lang === "AR" ? "المجموعات المختارة" : "Selected collections"}
                        </div>

                        <div className="max-h-[200px] overflow-y-auto">
                          {selectedCategories.map((item, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between px-4 py-3 border-b last:border-0"
                            >
                              <span className="text-sm font-medium">{item.name}</span>

                              <button
                                onClick={() =>
                                  setSelectedCategories(prev =>
                                    prev.filter((_, index) => index !== i)
                                  )
                                }
                                className="text-xs text-red-500"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>

              {/* SECTION 4 */}
              <div className={card}>
                <h3 className="text-[15px] font-semibold text-[#202223]">
                  {lang==="AR" ? "الحد الأدنى للشراء" : "Minimum purchase"}
                </h3>

                <p className={subtitle}>
                  {lang==="AR"
                    ? "حدد شروط الحد الأدنى لتفعيل الخصم"
                    : "Set minimum requirements to activate discount"}
                </p>

                <div className="space-y-2">

                  {/* NONE */}
                  <label className={`border rounded-[12px] p-3 flex gap-3 cursor-pointer transition
                    ${minRequirement==="none" ? "border-[#008060] bg-[#eef7f3]" : "border-[#e1e3e5]"}`}>
                    <input type="radio" checked={minRequirement==="none"} onChange={()=>setMinRequirement("none")} />
                    <div>
                      <div className="text-sm font-medium">No minimum requirements</div>
                      <div className="text-xs text-[#6d7175]">Discount applies without conditions</div>
                    </div>
                  </label>

                  {/* AMOUNT */}
                  <div className={`border rounded-[12px] p-3 ${minRequirement==="amount" ? "border-[#008060] bg-[#eef7f3]" : ""}`}>
                    <label className="flex gap-3">
                      <input type="radio" checked={minRequirement==="amount"} onChange={()=>setMinRequirement("amount")} />
                      <div>
                        <div className="text-sm font-medium">Minimum purchase amount</div>
                        <div className="text-xs text-[#6d7175]">Customer must spend a minimum amount</div>
                      </div>
                    </label>

                    {minRequirement==="amount" && (
                      <div className="mt-3">
                        <label className="text-xs text-[#6d7175] block mb-1">Amount</label>
                        <input className={input} value={minAmount} onChange={(e)=>setMinAmount(e.target.value)} />
                      </div>
                    )}
                  </div>

                  {/* QTY */}
                  <div className={`border rounded-[12px] p-3 ${minRequirement==="qty" ? "border-[#008060] bg-[#eef7f3]" : ""}`}>
                    <label className="flex gap-3">
                      <input type="radio" checked={minRequirement==="qty"} onChange={()=>setMinRequirement("qty")} />
                      <div>
                        <div className="text-sm font-medium">Minimum quantity of items</div>
                        <div className="text-xs text-[#6d7175]">Customer must buy a minimum quantity</div>
                      </div>
                    </label>

                    {minRequirement==="qty" && (
                      <div className="mt-3">
                        <label className="text-xs text-[#6d7175] block mb-1">Quantity</label>
                        <input className={input} value={minQty} onChange={(e)=>setMinQty(e.target.value)} />
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* SECTION 5 */}
              <div className={card}>
               <h3 className="text-[15px] font-semibold text-[#202223]">
                {lang==="AR" ? "عدد مرات استخدام الخصم" : "Maximum discount uses"}
              </h3>

              <div className="space-y-2">

                {/* LIMIT */}
                <div className={`border rounded-[12px] p-3 ${usageLimitType==="limit" ? "border-[#008060] bg-[#eef7f3]" : ""}`}>
                  <label className="flex gap-3">
                    <input type="radio" checked={usageLimitType==="limit"} onChange={()=>setUsageLimitType("limit")} />
                    <div>
                      <div className="text-sm font-medium">
                        Limit number of uses
                      </div>
                      <div className="text-xs text-[#6d7175]">
                        Limit total usage count
                      </div>
                    </div>
                  </label>

                  {usageLimitType==="limit" && (
                    <div className="mt-3">
                      <label className="text-xs text-[#6d7175] block mb-1">Limit</label>
                      <input className={input} value={usageLimit} onChange={(e)=>setUsageLimit(e.target.value)} />
                    </div>
                  )}
                </div>

                {/* ONCE */}
                <label className={`border rounded-[12px] p-3 flex gap-3 ${usageLimitType==="once" ? "border-[#008060] bg-[#eef7f3]" : ""}`}>
                  <input type="radio" checked={usageLimitType==="once"} onChange={()=>setUsageLimitType("once")} />
                  <div>
                    <div className="text-sm font-medium">One use per customer</div>
                    <div className="text-xs text-[#6d7175]">Each customer can use once</div>
                  </div>
                </label>

              </div>
              </div>

              {/* SECTION 6 */}
              <div className={card}>
                <h3 className="text-[15px] font-semibold text-[#202223]">
                  {lang==="AR" ? "تاريخ التفعيل" : "Active dates"}
                </h3>

                <p className={subtitle}>
                  {lang==="AR"
                    ? "حدد متى يبدأ وينتهي هذا الخصم"
                    : "Set when this discount starts and ends"}
                </p>

                {/* START */}
                  <div>
                    <label className="text-xs text-[#6d7175] mb-1 block">
                      {lang==="AR" ? "تاريخ البدء" : "Start date"}
                    </label>

                    <div className="grid grid-cols-2 gap-3">

                      {/* DATE */}
                      <button
                        type="button"
                        onClick={() => {
                          setDateTarget("start");
                          setDateModalOpen(true);
                        }}
                        className="flex items-center gap-2 h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white hover:border-[#008060]"
                      >
                        <FaCalendarAlt className="text-[#6d7175]" />
                        <span>{formatDisplayDate(startDate)}</span>
                      </button>

                      {/* TIME */}
                      <button
                        type="button"
                        onClick={() => {
                            setTimeTarget("start");

                            setTempTime({
                              hour: 12,
                              minute: "00",
                              period: "AM",
                            });

                            setTimeModalOpen(true);
                          }}
                        className="flex items-center gap-2 h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white hover:border-[#008060]"
                      >
                        <FaClock className="text-[#6d7175]" />
                        <span>{formatDisplayTime(startTime)}</span>
                      </button>

                    </div>
                  </div>

                <label className="flex items-center gap-2 text-sm mt-2">
                  <input type="checkbox" checked={hasEnd} onChange={()=>setHasEnd(!hasEnd)} />
                  {lang==="AR" ? "تاريخ انتهاء" : "Set end date"}
                </label>

                {hasEnd && (
                  <div className="mt-3">

                    <label className="text-xs text-[#6d7175] mb-1 block">
                      {lang==="AR" ? "تاريخ الانتهاء" : "End date"}
                    </label>

                    <div className="grid grid-cols-2 gap-3">

                      <button
                        type="button"
                        onClick={() => {
                          setDateTarget("end");
                          setDateModalOpen(true);
                        }}
                        className="flex items-center gap-2 h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white hover:border-[#008060]"
                      >
                        <FaCalendarAlt className="text-[#6d7175]" />
                        <span>{formatDisplayDate(endDate)}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setTimeTarget("end");

                          setTempTime({
                            hour: 12,
                            minute: "00",
                            period: "AM",
                          });

                          setTimeModalOpen(true);
                        }}
                        className="flex items-center gap-2 h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white hover:border-[#008060]"
                      >
                        <FaClock className="text-[#6d7175]" />
                        <span>{formatDisplayTime(endTime)}</span>
                      </button>

                    </div>

                  </div>
                )}
              </div>

            </div>

            {/* ================= RIGHT ================= */}
            <div className="space-y-3">

{/* STATUS (EDIT ONLY) */}
{isEdit && (
  <div className={card}>
    <h3 className="text-[15px] font-semibold text-[#202223]">
      {lang === "AR" ? "الحالة" : "Status"}
    </h3>

    <p className={subtitle}>
      {lang === "AR"
        ? "حدد ما إذا كان الخصم نشطًا أو غير نشط"
        : "Control whether this discount is active or inactive"}
    </p>

    <div className="relative">
      <select
        value={isActive ? "active" : "inactive"}
        onChange={(e) => {
          setIsActive(e.target.value === "active" ? 1 : 0);
          setIsDirty(true);
        }}
        className={select}
      >
        <option value="active">
          {lang === "AR" ? "نشط" : "Active"}
        </option>

        <option value="inactive">
          {lang === "AR" ? "غير نشط" : "Inactive"}
        </option>
      </select>

      {/* Shopify arrow */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6d7175]">
        ▾
      </div>
    </div>

    {/* OPTIONAL VISUAL BADGE */}
    <div className="mt-2">
      <span
        className={`
          text-[11px] px-2.5 py-[3px] rounded-md font-medium
          ${isActive
            ? "bg-green-50 text-green-700"
            : "bg-gray-100 text-gray-600"}
        `}
      >
        {isActive
          ? (lang === "AR" ? "نشط" : "Active")
          : (lang === "AR" ? "غير نشط" : "Inactive")}
      </span>
    </div>
  </div>
)}

              <div className={card}>
                <h3 className="text-[15px] font-semibold text-[#202223]">Summary</h3>

                <div className="bg-[#f6f6f7] border border-[#e1e3e5] rounded-[14px] p-4 text-[14px] leading-relaxed text-[#202223]">
                    {buildSummary()}
                  </div>
              </div>

            </div>

          </div>
         </div>
        </main>
      </div>
{showLeaveConfirm && (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] border border-[#e1e3e5] shadow-xl">

      <div className="px-5 py-4 border-b">
        <h3 className="text-lg font-semibold">
          {lang === "AR" ? "مغادرة الصفحة؟" : "Leave page?"}
        </h3>
      </div>

      <div className="p-5 text-sm text-[#202223]">
        {lang === "AR"
          ? "لديك تغييرات غير محفوظة. إذا غادرت سيتم فقدانها."
          : "You have unsaved changes. If you leave, they will be lost."}
      </div>

      <div className="px-5 py-4 border-t flex justify-end gap-3">

        <button
          onClick={() => setShowLeaveConfirm(false)}
          className="px-4 py-2 border rounded"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            setShowLeaveConfirm(false);
            pendingNavigation && pendingNavigation();
          }}
          className="px-4 py-2 bg-black text-white rounded"
        >
          {lang === "AR" ? "مغادرة" : "Leave"}
        </button>

      </div>
    </div>
  </div>
)}

      {selectionModalOpen && appliesToType === "products" && (
  <div
    className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
    onClick={() => setSelectionModalOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-[640px] bg-white rounded-[18px] border shadow-xl overflow-hidden"
    >

      {/* HEADER */}
      <div className="px-6 py-4 border-b bg-[#f6f6f7] flex justify-between">
        <h3 className="font-semibold">
          {lang === "AR" ? "اختر المنتجات" : "Select products"}
        </h3>
        <button onClick={() => setSelectionModalOpen(false)}>✕</button>
      </div>

      {/* SEARCH + FILTER */}
      <div className="p-4 border-b border-[#e1e3e5]">
        <div className="flex flex-col sm:flex-row gap-2">

          <div className="flex-1 h-[42px] border border-[#c9cccf] focus-within:border-[#008060] rounded-[12px] px-4 bg-white flex items-center gap-3 transition">
            <FaSearch className="text-[#6d7175] text-sm" />
            <input
              autoFocus
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder={lang === "AR" ? "ابحث..." : "Search products"}
              className="flex-1 outline-none text-sm text-[#202223]"
            />
          </div>
        </div>
      </div>

      {/* TABLE HEADER */}
      <div className="grid grid-cols-[1fr_100px_120px] px-6 py-3 text-xs font-semibold text-[#6d7175] border-b bg-[#f6f6f7]">
        <div>{lang === "AR" ? "المنتج" : "Product"}</div>
        <div className="text-center">{lang === "AR" ? "الكمية" : "Qty"}</div>
        <div className="text-center">{lang === "AR" ? "السعر" : "Price"}</div>
      </div>

      {/* BODY */}
      <div className="h-[320px] overflow-y-auto">

        {filteredProducts.length === 0 ? (
          <div className="p-6 text-sm text-[#6d7175]">
            {lang === "AR" ? "لا توجد منتجات" : "No products"}
          </div>
        ) : (
          filteredProducts.map((p) => {
            const isOnSale = !!p.on_sale;
            const salePercent = Number(p.sale_percent || 0);
            const isSelected = selectedProducts.some((i) => i.id === p.id);

            const totalQty = Array.isArray(p.variants)
              ? p.variants.reduce((sum, v) => sum + Number(v.stock_qty || 0), 0)
              : 0;

            return (
              <React.Fragment key={p.id}>
                <div
                  onClick={() => {
                    if (isOnSale) return;

                    setSelectedProducts((prev) => {
                      if (isSelected) {
                        return prev.filter((i) => i.id !== p.id);
                      }

                      return [
                        ...prev,
                        {
                          id: p.id,
                          name: lang === "AR" ? p.name_ar : p.name_en,
                        },
                      ];
                    });
                  }}
                  className={`grid grid-cols-[1fr_100px_120px] items-center px-6 py-3 border-b transition ${
                    isOnSale
                      ? "bg-[#fff8f8] cursor-not-allowed"
                      : isSelected
                      ? "bg-[#eef7f3] cursor-pointer"
                      : "hover:bg-[#f6f6f7] cursor-pointer"
                  }`}
                >
                  {/* PRODUCT */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#f6f6f7] border border-[#e1e3e5] flex-shrink-0">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#202223] flex items-center gap-2 truncate">
                        <span className="truncate">
                          {lang === "AR" ? p.name_ar : p.name_en}
                        </span>

                        {isOnSale && (
                          <span className="shrink-0 text-[10px] px-2 py-[2px] rounded-full bg-[#ffe7e7] text-[#d72c0d] font-semibold border border-[#ffc9c9]">
                            {salePercent}% OFF
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-[#6d7175] truncate">
                        {p.public_id}
                      </div>
                    </div>
                  </div>

                  {/* QTY */}
                  <div className="text-center text-sm text-[#202223]">
                    {totalQty}
                  </div>

                  {/* PRICE */}
                  <div className="text-center text-sm font-medium text-[#202223]">
                    ${Number(p.base_price || 0).toFixed(2)}
                  </div>
                </div>

                {isOnSale ? (
                  <div className="px-6 py-3 border-b bg-[#fffdfd]">
                    <div className="flex items-center justify-between gap-3 rounded-[12px] border border-[#ffd6d6] bg-gradient-to-r from-[#fff1f1] to-[#fff8f8] px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-[#d72c0d]">
                          {lang === "AR"
                            ? "هذا المنتج يحتوي على خصم نشط"
                            : "This product already has an active sale"}
                        </div>

                        <div className="text-[11px] text-[#8a3b2f] mt-0.5">
                          {lang === "AR"
                            ? "يجب إزالة الخصم القديم أولاً لإضافة خصم جديد."
                            : "You have to remove the old sale from this product before adding a new one."}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemoveSaleProduct(p);
                          setConfirmRemoveSaleOpen(true);
                        }}
                        className="shrink-0 h-[32px] px-3 rounded-[8px] border border-[#f1b5b5] bg-white text-[#d72c0d] text-xs font-medium hover:bg-[#fff5f5] transition"
                      >
                        {lang === "AR" ? "إزالة الخصم القديم" : "Remove old sale"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            );
          })
        )}

      </div>

      {/* FOOTER */}
      <div className="px-6 py-4 border-t flex justify-end">
        <button
          onClick={() => setSelectionModalOpen(false)}
          className="px-4 py-2 bg-black text-white rounded"
        >
          {lang === "AR" ? "تم" : "Done"}
        </button>
      </div>

    </div>
  </div>
)}

{showConfirm && (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] border border-[#e1e3e5] shadow-xl">

      <div className="px-5 py-4 border-b">
        <h3 className="text-lg font-semibold text-[#202223]">
          {lang === "AR" ? "تأكيد حفظ الخصم" : "Confirm discount save"}
        </h3>
      </div>

      <div className="p-5 text-sm text-[#202223] space-y-2">
        <p>
          <strong>{lang === "AR" ? "نوع الخصم:" : "Discount type:"}</strong>{" "}
          {discountType === "percentage" ? "%" : "$"}
        </p>
        <p>
          <strong>{lang === "AR" ? "القيمة:" : "Value:"}</strong> {discountValue}
        </p>
        <p>
          <strong>{lang === "AR" ? "ينطبق على:" : "Applies to:"}</strong>{" "}
          {appliesToType === "products"
            ? (lang === "AR" ? "منتجات" : "Products")
            : (lang === "AR" ? "مجموعات" : "Collections")}
        </p>
      </div>

      <div className="px-5 py-4 border-t flex justify-end gap-3">
        <button
          onClick={() => setShowConfirm(false)}
          className="px-4 py-2 border rounded"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-4 py-2 rounded text-white ${
            saving ? "bg-gray-400 cursor-not-allowed" : "bg-black"
          }`}
        >
          {saving ? (lang === "AR" ? "جارٍ الحفظ..." : "Saving...") : (lang === "AR" ? "تأكيد" : "Confirm")}
        </button>
      </div>

    </div>
  </div>
)}

{selectionModalOpen && appliesToType === "collections" && (
  <div
    className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
    onClick={() => setSelectionModalOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-[500px] bg-white rounded-[18px] border shadow-xl overflow-hidden"
    >

      {/* HEADER */}
      <div className="px-6 py-4 border-b bg-[#f6f6f7] flex justify-between">
        <h3 className="font-semibold">Select collection</h3>
        <button onClick={() => setSelectionModalOpen(false)}>✕</button>
      </div>

      {/* SEARCH */}
        <div className="p-4 border-b border-[#e1e3e5]">
          <div className="h-[42px] border border-[#8c9196] rounded-[12px] px-4 bg-white flex items-center gap-3">
            <FaSearch className="text-[#6d7175] text-sm" />
            <input
              autoFocus
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder={lang === "AR" ? "ابحث..." : "Search categories"}
              className="flex-1 outline-none text-sm text-[#202223]"
            />
          </div>
        </div>

        {/* LIST */}
        <div className="max-h-[400px] overflow-y-auto">
          {filteredCategories.length === 0 ? (
            <div className="px-4 py-5 text-sm text-[#6d7175]">
              {lang === "AR" ? "لا توجد نتائج" : "No results"}
            </div>
          ) : (
            filteredCategories.map((c) => {
              const isSelected = selectedCategories.some((item) => item.id === c.id);

              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setSelectedCategories((prev) => {
                      if (isSelected) {
                        return prev.filter((item) => item.id !== c.id);
                      }

                      return [
                        ...prev,
                        {
                          id: c.id,
                          name: lang === "AR" ? c.name_ar : c.name_en,
                        },
                      ];
                    });
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 border-b transition ${
                    isSelected ? "bg-[#eef7f3]" : "hover:bg-[#f6f6f7]"
                  }`}
                >
                  <span className="text-sm font-medium text-[#202223]">
                    {lang === "AR" ? c.name_ar : c.name_en}
                  </span>

                  <div
                    className={`w-5 h-5 rounded border flex items-center justify-center text-[11px] ${
                      isSelected
                        ? "bg-[#202223] border-[#202223] text-white"
                        : "border-[#c9cccf] text-transparent"
                    }`}
                  >
                    ✓
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end bg-white">
          <button
            onClick={() => setSelectionModalOpen(false)}
            className="px-4 py-2 bg-black text-white rounded"
          >
            {lang === "AR" ? "تم" : "Done"}
          </button>
        </div>

    </div>
  </div>
)}

      {dateModalOpen && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setDateModalOpen(false)}>
   <div
  className="w-[340px] bg-white rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)] overflow-hidden"
  onClick={(e) => e.stopPropagation()}
>

      {/* HEADER */}
      <div className="px-5 py-4 border-b">
        <h3 className="text-sm font-semibold">
          {lang === "AR" ? "اختر التاريخ" : "Select date"}
        </h3>
      </div>

      {/* BODY */}
      <div className="p-4">
        <CalendarRangePicker
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          setRangeStart={setRangeStart}
          setRangeEnd={setRangeEnd}
        />
      </div>

      {/* FOOTER */}
      <div className="px-4 py-3 flex justify-end gap-2 border-t">

        <button
          onClick={() => setDateModalOpen(false)}
          className="h-[36px] px-4 border rounded-lg"
        >
          Cancel
        </button>

        <button
          onClick={() => {
            if (!rangeStart) return;

            const date = `${rangeStart.getFullYear()}-${String(rangeStart.getMonth()+1).padStart(2,"0")}-${String(rangeStart.getDate()).padStart(2,"0")}`;

            if (dateTarget === "start") setStartDate(date);
            if (dateTarget === "end") setEndDate(date);

            setDateModalOpen(false);
          }}
          className="h-[36px] px-4 bg-black text-white rounded-lg"
        >
          Apply
        </button>

      </div>
    </div>
  </div>
)}

{confirmRemoveSaleOpen && (
  <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center">
    <div className="w-full max-w-[400px] bg-white rounded-[18px] p-5">

      <h3 className="font-semibold mb-2">
        {lang === "AR" ? "إزالة الخصم" : "Remove sale"}
      </h3>

      <p className="text-sm text-[#6d7175] mb-5">
        {lang === "AR"
          ? "هل تريد إزالة الخصم من هذا المنتج؟"
          : "Are you sure you want to remove the sale from this product?"}
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => setConfirmRemoveSaleOpen(false)}
          className="px-4 py-2 border rounded"
        >
          Cancel
        </button>

        <button
          onClick={async () => {
            try {
              await removeProductsFromSale([removeSaleProduct.id]);

              setProducts(prev =>
                prev.map(p =>
                  p.id === removeSaleProduct.id
                    ? { ...p, on_sale: false, sale_percent: 0 }
                    : p
                )
              );

              setConfirmRemoveSaleOpen(false);
            } catch (e) {
              console.error(e);
            }
          }}
          className="px-4 py-2 bg-black text-white rounded"
        >
          {lang === "AR" ? "تأكيد" : "Confirm"}
        </button>
      </div>

    </div>
  </div>
)}

{timeModalOpen && (
 <div
  className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setTimeModalOpen(false)}
>
    <div
  className="w-[320px] bg-white rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)]"
  onClick={(e) => e.stopPropagation()}
>

      {/* HEADER */}
      <div className="px-5 py-4 border-b">
        <h3 className="text-sm font-semibold">
          {lang === "AR" ? "اختر الوقت" : "Select time"}
        </h3>
      </div>

      {/* BODY */}
      <div className="p-4">
        <TimePicker
            tempTime={tempTime}
            setTempTime={setTempTime}
          />
      </div>

      {/* FOOTER */}
      <div className="px-4 py-3 flex justify-end">
        <button
          onClick={() => {
            let hour = tempTime.hour;

            if (tempTime.period === "PM" && hour !== 12) hour += 12;
            if (tempTime.period === "AM" && hour === 12) hour = 0;

            const final = `${String(hour).padStart(2, "0")}:${tempTime.minute}`;

            if (timeTarget === "start") setStartTime(final);
            if (timeTarget === "end") setEndTime(final);

            setTimeModalOpen(false);
          }}
          className="h-[36px] px-4 bg-black text-white rounded-lg"
        >
          Done
        </button>
      </div>

    </div>
  </div>
)}
    </div>
  );
}

export default AdminCreateDiscount;